import { Button } from '@neo/primitives/button'
import { DialogHeader, DialogTitle } from '@neo/primitives/dialog'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { produceWithAutoFreezeDisabled } from '@shared/utils'
import { type Node, useUpdateNodeInternals } from '@xyflow/react'
import { ArrowLeft, Slack } from 'lucide-react'
import { FlowSourceTypes, FlowTypes } from 'v2/types'
import { getDefaultStartBaseNode } from '../config/nodes.config'
import { SlackChannelTypes, SlackTriggerConfig } from '../config/trigger-node.config'
import { useFlowStore } from '../store'
import { FlowNodeTypes, type NodeDataBase, type StartNodeData } from '../types'
import { getChannelTypeFromFlowType, getTriggerTypeFromPhloType, refreshNodeVars } from '../utils'

interface ExternalAppsConfigProps {
  appType: FlowTypes
  onBack: () => void
  onClose: () => void
}

export const ExternalAppsConfig = ({ appType, onBack, onClose }: ExternalAppsConfigProps) => {
  const updateNodeInternals = useUpdateNodeInternals()

  const handleSlackTriggerSelect = (selectedEvent: SlackChannelTypes) => {
    const { setCurrentFlowType, flowInstance, setAgentMeta, selectNode, discardChanges } =
      useFlowStore.getState()

    // Get the current flow type before changing it
    const currentFlowType = useFlowStore.getState().currentFlowType

    // Clear any existing selection and unsaved changes
    discardChanges()

    // Update the flow type to Slack
    setCurrentFlowType(FlowTypes.incoming_slack)

    // Find both the trigger and start nodes from the flow
    const [triggerNode, startNode] = (flowInstance?.getNodes() || []).reduce<
      [Node<NodeDataBase<unknown>> | undefined, Node<NodeDataBase<unknown>> | undefined]
    >(
      (acc, node) => {
        if (node.type === FlowNodeTypes.select_trigger) acc[0] = node
        else if (node.type === FlowNodeTypes.start) acc[1] = node
        return acc
      },
      [undefined, undefined],
    )

    // Set agent meta for Slack channel
    if (!startNode) {
      setAgentMeta({
        defaultChannel: getChannelTypeFromFlowType(FlowTypes.incoming_slack),
      })
    }

    // Find the edge connected to start node to update its source handle
    const startNodeEdge = flowInstance?.getEdges().find((edge) => edge.source === startNode?.id)

    // Update the edge's source handle to match new trigger type
    if (startNodeEdge) {
      flowInstance?.updateEdge(startNodeEdge.id, {
        sourceHandle: FlowSourceTypes[FlowTypes.incoming_slack],
      })
      if (startNode) updateNodeInternals(startNode?.id)
    }

    // Reset trigger node to default state while preserving position
    if (triggerNode) {
      const newNodeState = getDefaultStartBaseNode({
        triggerNode,
        phloType: FlowTypes.incoming_slack,
      })
      // Set the selected Slack trigger event in the new node state
      newNodeState.data.config.slack_trigger_event = selectedEvent
      newNodeState.data.config.slack_channel_id = null
      newNodeState.data.config.slack_reply_in_thread = selectedEvent === SlackChannelTypes.DIRECT

      flowInstance?.updateNode(triggerNode.id, newNodeState)
      updateNodeInternals(triggerNode?.id)
    } else if (startNode) {
      // If start node is there, update its trigger type and add the Slack trigger event
      flowInstance?.updateNode(
        startNode.id,
        produceWithAutoFreezeDisabled(
          startNode as Node<StartNodeData>,
          (draftNode: Node<StartNodeData>) => {
            if (draftNode.data.config) {
              const triggerValue = getTriggerTypeFromPhloType(FlowTypes.incoming_slack)
              draftNode.data.config.triggers = triggerValue ? [triggerValue] : []
              // Set the selected Slack trigger event
              draftNode.data.config.slack_trigger_event = selectedEvent
              draftNode.data.config.slack_channel_id = null
              draftNode.data.config.slack_reply_in_thread =
                selectedEvent === SlackChannelTypes.DIRECT
            }
            return undefined
          },
        ),
      )
      updateNodeInternals(startNode?.id)
    }

    // Get the node that should be selected
    const nodeToSelect = triggerNode || startNode

    // Refresh node vars
    if (nodeToSelect) {
      refreshNodeVars(nodeToSelect?.id)
    }

    // Close the trigger dialog
    onClose()

    // Show confirmation modal for trigger changes (skip for API requests)
    if (
      currentFlowType &&
      currentFlowType !== 'omni' &&
      currentFlowType !== FlowTypes.incoming_slack &&
      currentFlowType !== FlowTypes.api_request
    ) {
      useFlowStore.getState().setShowTriggerChangeConfirmationModal(true, {
        oldTriggerType: currentFlowType,
        newTriggerType: FlowTypes.incoming_slack,
      })
    }

    // Automatically select the start node to open its sidebar
    setTimeout(() => {
      if (nodeToSelect) {
        selectNode(nodeToSelect.id)
      }
    }, 100)
  }

  const appTitleMap = {
    [FlowTypes.incoming_slack]: 'Slack',
  }

  const renderSlackConfig = () => (
    <div className="px-6 pb-6 flex flex-col gap-4">
      {Object.entries(SlackTriggerConfig).map(([triggerType, triggerConfig]) => (
        <Button
          key={triggerType}
          variant="ghost"
          className="font-inter normal-case tracking-normal flex items-center gap-3 px-2 py-[6px] rounded-lg h-auto w-full justify-start"
          onClick={() => handleSlackTriggerSelect(triggerType as SlackChannelTypes)}
          data-testid={`slack-trigger-${triggerType}`}
        >
          <div className="w-8 h-8 rounded bg-flow-logic_bg flex items-center justify-center">
            <Slack className="text-brand-text w-4 h-4" />
          </div>
          <div className="flex flex-col text-left gap-1">
            <span className="text-p-400">{triggerConfig.title}</span>
            <span className="text-xs-400 text-tertiary">{triggerConfig.description}</span>
          </div>
        </Button>
      ))}
    </div>
  )

  const renderAppConfig = () => {
    switch (appType) {
      case FlowTypes.incoming_slack:
        return renderSlackConfig()
      default:
        return (
          <div className="px-6 pb-6">Configuration for this external app is not available.</div>
        )
    }
  }

  return (
    <>
      <DialogHeader className="p-6 pb-0">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="font-inter normal-case tracking-normal p-1 h-auto hover:bg-transparent"
            data-testid="button-back"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <DialogTitle className="text-h4-500">
              {appTitleMap[appType as keyof typeof appTitleMap]}
            </DialogTitle>
          </div>
        </div>
      </DialogHeader>

      <ScrollArea className="flex-1 overflow-auto">{renderAppConfig()}</ScrollArea>
    </>
  )
}
